// Período unificado para Contas a Pagar e Contas a Receber.
// Modelo: date range (YYYY-MM-DD) + helpers de preset + navegação ◄ ►.
//
// Aplicado a ambas as telas (CR/CP) pra dar a mesma UX de filtro de período,
// espelhando o navegador da Conta Azul + ganhando presets ricos.

export interface Periodo {
  ini: string;   // YYYY-MM-DD
  fim: string;   // YYYY-MM-DD
  rotulo: string;
}

function pad(n: number): string { return String(n).padStart(2, '0'); }
function ultimoDiaDoMes(y: number, m1to12: number): number {
  return new Date(y, m1to12, 0).getDate();
}
function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ─── Presets ─────────────────────────────────────────────────────────────────

export function presetHoje(): Periodo {
  const hoje = isoLocal(new Date());
  return { ini: hoje, fim: hoje, rotulo: 'Hoje' };
}

export function presetSemana(): Periodo {
  // Segunda a domingo da semana corrente
  const d = new Date();
  const dow = d.getDay();                 // 0=dom ... 6=sáb
  const desdeSegunda = (dow + 6) % 7;     // dom→6, seg→0, ter→1...
  const seg = new Date(d.getTime() - desdeSegunda * 24 * 3600 * 1000);
  const dom = new Date(seg.getTime() + 6 * 24 * 3600 * 1000);
  return { ini: isoLocal(seg), fim: isoLocal(dom), rotulo: 'Esta semana' };
}

export function presetMesAtual(): Periodo {
  const d = new Date();
  const ini = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
  const fim = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(ultimoDiaDoMes(d.getFullYear(), d.getMonth() + 1))}`;
  return { ini, fim, rotulo: 'Este mês' };
}

export function presetUltimoMes(): Periodo {
  const d = new Date();
  d.setDate(1);                 // evita "mês 28-29-30-31 não existe no anterior"
  d.setMonth(d.getMonth() - 1);
  const ini = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
  const fim = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(ultimoDiaDoMes(d.getFullYear(), d.getMonth() + 1))}`;
  return { ini, fim, rotulo: 'Mês passado' };
}

export function presetTrimestre(): Periodo {
  const d = new Date();
  const tri = Math.floor(d.getMonth() / 3);   // 0, 1, 2, 3
  const inicioMes = tri * 3 + 1;              // 1, 4, 7, 10
  const fimMes = inicioMes + 2;               // 3, 6, 9, 12
  const ini = `${d.getFullYear()}-${pad(inicioMes)}-01`;
  const fim = `${d.getFullYear()}-${pad(fimMes)}-${pad(ultimoDiaDoMes(d.getFullYear(), fimMes))}`;
  return { ini, fim, rotulo: 'Trimestre' };
}

export function presetAnoAtual(): Periodo {
  const y = new Date().getFullYear();
  return { ini: `${y}-01-01`, fim: `${y}-12-31`, rotulo: 'Ano' };
}

export function presetProximos30(): Periodo {
  const hoje = new Date();
  const ini = isoLocal(hoje);
  const fim = new Date(hoje.getTime() + 30 * 24 * 3600 * 1000);
  return { ini, fim: isoLocal(fim), rotulo: 'Próx. 30 dias' };
}

// Ordem canônica dos presets pros botões na UI (CR e CP).
export const PRESETS_PERIODO: Array<{ k: string; fn: () => Periodo }> = [
  { k: 'Hoje',          fn: presetHoje },
  { k: 'Esta semana',   fn: presetSemana },
  { k: 'Este mês',      fn: presetMesAtual },
  { k: 'Mês passado',   fn: presetUltimoMes },
  { k: 'Trimestre',     fn: presetTrimestre },
  { k: 'Ano',           fn: presetAnoAtual },
  { k: 'Próx. 30 dias', fn: presetProximos30 },
];

// ─── Navegador ◄ ► ───────────────────────────────────────────────────────────

// Granularidade detectada do range — determina o PASSO do ◄ ►.
// Todo range navega: 'custom' desliza pela própria largura em dias (nunca congela).
export type ModoNavegador = 'dia' | 'semana' | 'mes' | 'trimestre' | 'ano' | 'custom';

const MESES_BR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// ── Helpers de data em horário LOCAL (sem fuso — evita drift de UTC) ──
function parseIsoLocal(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function addDiasIso(s: string, n: number): string {
  const d = parseIsoLocal(s);
  d.setDate(d.getDate() + n);
  return isoLocal(d);
}
// Soma meses preservando o dia (usado só p/ inícios de mês/trimestre — dia 1).
function addMesesIso(s: string, n: number): string {
  const [y, m, d] = s.split('-').map(Number);
  let ny = y, nm = m + n;
  while (nm < 1)  { nm += 12; ny--; }
  while (nm > 12) { nm -= 12; ny++; }
  return `${ny}-${pad(nm)}-${pad(d)}`;
}
function diasEntreIso(a: string, b: string): number {
  return Math.round((parseIsoLocal(b).getTime() - parseIsoLocal(a).getTime()) / 86400000);
}

// Detecta a granularidade do range (dia, semana, mês civil, trimestre civil, ano,
// ou custom). Determina como o ◄ ► navega e o rótulo exibido.
export function detectarModoNavegador(ini: string, fim: string): ModoNavegador {
  if (!ini || !fim) return 'custom';
  const [y1, m1, d1] = ini.split('-').map(Number);
  const [y2, m2, d2] = fim.split('-').map(Number);
  if ([y1, m1, d1, y2, m2, d2].some(Number.isNaN)) return 'custom';
  // ano cheio (01-jan → 31-dez)
  if (ini === `${y1}-01-01` && fim === `${y1}-12-31`) return 'ano';
  // trimestre civil (jan-mar, abr-jun, jul-set, out-dez)
  if (y1 === y2 && d1 === 1 && (m1 === 1 || m1 === 4 || m1 === 7 || m1 === 10)
      && m2 === m1 + 2 && d2 === ultimoDiaDoMes(y2, m2)) return 'trimestre';
  // mês cheio (dia 1 → último dia)
  if (y1 === y2 && m1 === m2 && d1 === 1 && d2 === ultimoDiaDoMes(y1, m1)) return 'mes';
  // dia único
  if (ini === fim) return 'dia';
  // semana (span de 7 dias inclusive)
  if (diasEntreIso(ini, fim) === 6) return 'semana';
  return 'custom';
}

function brData(s: string): string {
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

export function rotuloNavegador(ini: string, fim: string): string {
  const modo = detectarModoNavegador(ini, fim);
  if (modo === 'ano') return ini.slice(0, 4);
  if (modo === 'trimestre') {
    const [y, m1] = ini.split('-').map(Number);
    return `${MESES_BR[m1 - 1]}–${MESES_BR[m1 + 1]} ${y}`;
  }
  if (modo === 'mes') {
    const [y, m] = ini.split('-').map(Number);
    return `${MESES_BR[m - 1]} ${y}`;
  }
  if (modo === 'dia') return brData(ini);
  // semana + custom → intervalo "08/05/26–14/05/26"
  return ini && fim ? `${brData(ini)}–${brData(fim)}` : '—';
}

// Avança/regride o range em 1 unidade conforme a granularidade detectada.
// NUNCA retorna null: dia→±1d, semana→±7d, mês/trimestre/ano por unidade civil,
// custom desliza pela própria largura (span em dias, contíguo).
export function navegarPeriodo(ini: string, fim: string, delta: number): Periodo {
  const modo = detectarModoNavegador(ini, fim);
  if (modo === 'ano') {
    const y = parseInt(ini.slice(0, 4)) + delta;
    return { ini: `${y}-01-01`, fim: `${y}-12-31`, rotulo: String(y) };
  }
  if (modo === 'trimestre') {
    const novoIni = addMesesIso(ini, 3 * delta);        // ini é sempre dia 1 do mês inicial
    const [ny, nm1] = novoIni.split('-').map(Number);
    const nm2 = nm1 + 2;
    const novoFim = `${ny}-${pad(nm2)}-${pad(ultimoDiaDoMes(ny, nm2))}`;
    return { ini: novoIni, fim: novoFim, rotulo: rotuloNavegador(novoIni, novoFim) };
  }
  if (modo === 'mes') {
    const [y, m] = ini.split('-').map(Number);
    let novoM = m + delta, novoY = y;
    while (novoM < 1)  { novoM += 12; novoY--; }
    while (novoM > 12) { novoM -= 12; novoY++; }
    const novoIni = `${novoY}-${pad(novoM)}-01`;
    const novoFim = `${novoY}-${pad(novoM)}-${pad(ultimoDiaDoMes(novoY, novoM))}`;
    return { ini: novoIni, fim: novoFim, rotulo: `${MESES_BR[novoM - 1]} ${novoY}` };
  }
  // dia / semana / custom → desliza pela largura do range (contíguo).
  const span = diasEntreIso(ini, fim) + 1;              // dia=1, semana=7, custom=N
  const novoIni = addDiasIso(ini, span * delta);
  const novoFim = addDiasIso(fim, span * delta);
  return { ini: novoIni, fim: novoFim, rotulo: rotuloNavegador(novoIni, novoFim) };
}
